import { Box, IconButton } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { MdClose } from 'react-icons/md';

import DOMPurify from 'dompurify';
import colorContext from '../../Context/colorContext';
import { getNotices } from '../../services/avisos';
import getCookie from '../../utils/getCookies';
import { setCookie } from '../../utils/setCookie';

export interface INotice {
  titulo: string;
  conteudo: string;
  id: number;
}

export default function ModalAlertsDialog(): JSX.Element {
  const { colors } = useContext(colorContext);
  const useStyles = makeStyles((theme: Theme) => ({
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing(4),
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: theme.zIndex.modal,
    },
    paper: {
      width: '100%',
      maxWidth: 960,
      maxHeight: 'calc(100% - 64px)',
      display: 'flex',
      flexDirection: 'column',
      borderRadius: theme.shape.borderRadius,
      boxShadow: theme.shadows[24],
      overflow: 'hidden',
    },
    root: {
      backgroundColor: colors.colorBackgroundSecundary,
      overflowY: 'auto',
      borderTop: `1px solid ${colors.dividerColor}`,
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: colors.textBody,
    },
    titleDialog: {
      position: 'relative',
      color: colors.textBody,
      backgroundColor: colors.colorBackground,
      paddingTop: 20,
      paddingBottom: 20,
      paddingLeft: 24,
      paddingRight: 40,
      fontSize: '1.25rem',
      fontWeight: 500,
    },
    contentDialog: {
      color: colors.textBody,
      backgroundColor: colors.colorBackground,
      width: '100%',
      padding: 20,
    },
  }));
  const classes = useStyles();
  const [open, setOpen] = useState<boolean>(false);
  const [notice, setNotice] = useState<INotice | undefined>();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedElement = useRef<HTMLElement | null>(null);

  const loadCurrentNotices = async () => {
    try {
      const { data } = await getNotices();
      if (data) {
        setNotice(data);
      }
    } catch (error) {
      console.log(error);
    }
  };
  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    loadCurrentNotices();
  }, []);

  useEffect(() => {
    const noticeViewed = getCookie('notice-visualized');
    if (notice && noticeViewed !== notice.id.toString()) {
      setCookie('notice-visualized', notice.id.toString());
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [notice]);

  useEffect(() => {
    if (!open) return undefined;

    previouslyFocusedElement.current =
      document.activeElement as HTMLElement | null;
    closeButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocusedElement.current?.focus();
    };
  }, [open]);

  if (!open) {
    return <></>;
  }

  return (
    <Box
      className={classes.overlay}
      onMouseDown={event => {
        if (event.target === event.currentTarget) {
          handleClose();
        }
      }}
    >
      <Box
        className={classes.paper}
        role='dialog'
        aria-labelledby='alert-dialog-title'
      >
        <Box id='alert-dialog-title' className={classes.titleDialog}>
          {notice?.titulo}
          <IconButton
            ref={closeButtonRef}
            aria-label='close'
            className={classes.closeButton}
            onClick={handleClose}
          >
            <MdClose />
          </IconButton>
        </Box>
        <Box className={classes.root}>
          <Box
            className={classes.contentDialog}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(notice?.conteudo || '') }}
          />
        </Box>
      </Box>
    </Box>
  );
}
